import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import SkeletonGroup from './SkeletonGroup';
import './Skeleton.scss';

/**
 * TextSkeleton component - Use for paragraphs, labels, descriptions.
 *
 * This component creates realistic text placeholders with multiple lines,
 * where the last line is typically shorter to mimic real text patterns.
 * It's perfect for loading states of text content like descriptions,
 * paragraphs, or any multi-line text content.
 *
 * @component
 * @example
 * // Single line text
 * <TextSkeleton lines={1} />
 *
 * @example
 * // Multi-line paragraph
 * <TextSkeleton lines={3} />
 *
 * @example
 * // Custom line height and spacing
 * <TextSkeleton
 *   lines={4}
 *   lineHeight="20px"
 *   lastLineWidth="70%"
 *   spacing="sm"
 * />
 */
// Design token defaults (matching _variables.scss)
const TEXT_DEFAULTS = {
  lineHeight: '16px', // $skeleton-text-height-lg
  borderRadius: '4px', // $skeleton-border-radius-sm
};

const TextSkeleton = ({
  lines = 3,
  lineHeight = TEXT_DEFAULTS.lineHeight,
  lastLineWidth = '70%',
  spacing = 'sm',
  className = '',
  ...props
}) => {
  const textLines = Array.from({ length: lines }, (_, index) => {
    const isLastLine = index === lines - 1;
    const width = isLastLine ? lastLineWidth : '100%';

    return (
      <Skeleton
        key={index}
        width={width}
        height={lineHeight}
        borderRadius={TEXT_DEFAULTS.borderRadius}
        className='text-skeleton-line'
      />
    );
  });

  return (
    <SkeletonGroup
      direction='vertical'
      spacing={spacing}
      className={`text-skeleton ${className}`}
      {...props}
    >
      {textLines}
    </SkeletonGroup>
  );
};

TextSkeleton.propTypes = {
  /** Number of text lines to render */
  lines: PropTypes.number,

  /** Height of each text line */
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Width of the last line (mimics real text patterns) */
  lastLineWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Spacing between lines (xs, sm, md, lg, xl) */
  spacing: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default TextSkeleton;
